import React, { useState, useEffect, useCallback } from "react";
import "./style.css";
import Toast from "./Toast";
import ConfirmModal from "./ConfirmModal";

//get the localStorage data back
const getLocalData = () => {
  const lists = localStorage.getItem("mytodolist");

  if (lists) {
    return JSON.parse(lists);
  } else {
    return [];
  }
};

// Format a stored ISO timestamp into "Mon · 18 Apr 2026 · 12:34 PM"
const formatTaskTime = (isoString) => {
  if (!isoString) return null;
  const d = new Date(isoString);
  const day  = d.toLocaleDateString("en-US", { weekday: "short" });   // Mon
  const date = d.toLocaleDateString("en-GB",  { day: "2-digit", month: "short", year: "numeric" }); // 18 Apr 2026
  const time = d.toLocaleTimeString("en-US",  { hour: "2-digit", minute: "2-digit", hour12: true }); // 12:34 PM
  return `${day} · ${date} · ${time}`;
};

const Todo = () => {
  const MAX_LEN = 20;
  const [inputdata, setInputData] = useState("");
  const [items, setItems] = useState(getLocalData());
  const [isEditItem, setIsEditItem] = useState("");
  const [toggleButton, setToggleButton] = useState(false);
  const [toasts, setToasts] = useState([]);
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, mode: "single", taskId: null, taskName: "" });

  // ── Theme ───────────────────────────────────────────────────────────────
  const [theme, setTheme] = useState(() => localStorage.getItem("theme") || "dark");

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    localStorage.setItem("theme", theme);
  }, [theme]);

  const toggleTheme = () => setTheme((prev) => (prev === "dark" ? "light" : "dark"));

  // ── Toast helpers ────────────────────────────────────────────────────────────
  const addToast = useCallback((type, message) => {
    const id = Date.now().toString();
    setToasts((prev) => [...prev, { id, type, message }]);
  }, []);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  // ── Add / Edit item ──────────────────────────────────────────────────────────
  const addItem = () => {
    if (!inputdata.trim()) {
      alert("Plz Fill The Data");
    } else if (inputdata && toggleButton) {
      setItems(
        items.map((curElem) => {
          if (curElem.id === isEditItem) {
            return { ...curElem, name: inputdata, updatedAt: new Date().toISOString() };
          }
          return curElem;
        })
      );

      addToast("edit", `"${inputdata}" updated`);
      setInputData("");
      setIsEditItem(null);
      setToggleButton(false);
    } else {
      const myNewInputData = {
        id: new Date().getTime().toString(),
        name: inputdata,
        createdAt: new Date().toISOString(),
      };
      setItems([...items, myNewInputData]);
      addToast("add", `"${inputdata}" added to your list`);
      setInputData("");
    }
  };

  // ── Edit item ────────────────────────────────────────────────────────────────
  const editItem = (index) => {
    const item_todo_edited = items.find((curElem) => {
      return curElem.id === index;
    });
    setInputData(item_todo_edited.name);
    setIsEditItem(index);
    setToggleButton(true);
  };

  // ── Delete item (show confirmation first) ───────────────────────────────────
  const requestDelete = (index) => {
    const target = items.find((curElem) => curElem.id === index);
    if (!target) return;
    setConfirmModal({ isOpen: true, mode: "single", taskId: index, taskName: target.name });
  };

  const confirmDelete = () => {
    const { taskId, taskName } = confirmModal;
    setItems((prev) => prev.filter((curElem) => curElem.id !== taskId));
    addToast("delete", `"${taskName}" removed`);
    setConfirmModal({ isOpen: false, mode: "single", taskId: null, taskName: "" });
  };

  const cancelDelete = () => {
    setConfirmModal({ isOpen: false, mode: "single", taskId: null, taskName: "" });
  };

  // ── Remove all (show confirmation first) ─────────────────────────────────────
  const requestRemoveAll = () => {
    if (items.length === 0) return;
    setConfirmModal({ isOpen: true, mode: "all", taskId: null, taskName: "" });
  };

  const confirmRemoveAll = () => {
    setItems([]);
    addToast("delete", "All tasks cleared");
    setConfirmModal({ isOpen: false, mode: "single", taskId: null, taskName: "" });
  };

  // ── Persist to localStorage ──────────────────────────────────────────────────
  useEffect(() => {
    localStorage.setItem("mytodolist", JSON.stringify(items));
  }, [items]);

  return (
    <>
      {/* Theme toggle */}
      <button
        className="theme-toggle"
        onClick={toggleTheme}
        title={theme === "dark" ? "Switch to Light Mode" : "Switch to Dark Mode"}
        aria-label="Toggle theme"
      >
        <i className={theme === "dark" ? "fas fa-sun" : "fas fa-moon"}></i>
      </button>

      {/* Toast notifications */}
      <Toast toasts={toasts} removeToast={removeToast} />

      {/* Delete confirmation modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        mode={confirmModal.mode}
        taskName={confirmModal.taskName}
        taskCount={items.length}
        onConfirm={confirmModal.mode === "all" ? confirmRemoveAll : confirmDelete}
        onCancel={cancelDelete}
      />

      <div className="main-div">
        <div className="child-div">
          <figure>
            <img src="./images/todo.png" alt="todologo" />
            <figcaption>Add Your Memories Here🙌</figcaption>
            <h3>Secured By manishinc</h3>
          </figure>
          <div className="addItems">
            <div className="input-wrapper">
              <input
                type="text"
                placeholder="📒Add Item"
                className="form-control"
                value={inputdata}
                maxLength={MAX_LEN}
                onChange={(event) =>
                  setInputData(event.target.value.slice(0, MAX_LEN))
                }
                onKeyDown={(e) => e.key === "Enter" && addItem()}
              />
              <span
                className={`char-counter ${
                  inputdata.length === MAX_LEN
                    ? "char-counter--full"
                    : inputdata.length >= MAX_LEN - 5
                    ? "char-counter--warn"
                    : ""
                }`}
              >
                {inputdata.length}/{MAX_LEN}
              </span>
            </div>
            {toggleButton ? (
              <i className="fa fa-edit add-btn" onClick={addItem}></i>
            ) : (
              <i className="fa fa-plus add-btn" onClick={addItem}></i>
            )}
          </div>
          {/* show our items */}
          <div className="showItems">
            {items.map((curElem) => {
              return (
                <div className="eachItem" key={curElem.id}>
                  <div className="item-info">
                    <h3>{curElem.name}</h3>
                    <div className="item-timestamp">
                      {curElem.updatedAt ? (
                        <><i className="fas fa-pen-nib"></i> {formatTaskTime(curElem.updatedAt)}</>
                      ) : (
                        <><i className="fas fa-clock"></i> {formatTaskTime(curElem.createdAt)}</>
                      )}
                    </div>
                  </div>
                  <div className="todo-btn">
                    <i
                      className="far fa-edit add-btn"
                      onClick={() => editItem(curElem.id)}
                    ></i>
                    <i
                      className="far fa-trash-alt add-btn"
                      onClick={() => requestDelete(curElem.id)}
                    ></i>
                  </div>
                </div>
              );
            })}
          </div>
          {/* remove all button */}
          <div className="showItems">
            <button
              className="btn effect04"
              data-sm-link-text="Remove All"
              onClick={requestRemoveAll}
            >
              <span>Remove All</span>
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Todo;
